import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useSubmissionDocuments,
  updateDocumentCategory,
} from '@/hooks/submissions/useSubmissionDocuments';
import DocumentsTable, {
  DOCUMENTS_ROW_ID_PREFIX,
  getDocumentStatusBadge,
  type DocumentWithStatus,
} from '@/components/DocumentsTable';

interface DocumentsProps {
  submissionId: string | null;
  compact?: boolean;
}

const Documents = ({ submissionId, compact = true }: DocumentsProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight') ?? undefined;
  const { documents, isLoading, error, refetch } =
    useSubmissionDocuments(submissionId);

  const documentsWithStatus: DocumentWithStatus[] = useMemo(() => {
    return documents.map(doc => {
      const badge = getDocumentStatusBadge(doc.document_status);
      return {
        ...doc,
        statusLabel: badge.label,
        statusClassName: badge.className,
      };
    });
  }, [documents]);

  useEffect(() => {
    if (!highlightId || isLoading) return;
    const el = document.getElementById(`${DOCUMENTS_ROW_ID_PREFIX}${highlightId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Remove the highlight param so it doesn't persist on back/forward navigation
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('highlight');
      return next;
    }, { replace: true });
  }, [highlightId, isLoading, setSearchParams]);

  const handleCategoryUpdate = async (
    documentId: string,
    documentSubmissionId: string,
    category: string
  ) => {
    await updateDocumentCategory(documentSubmissionId, documentId, category);
    await refetch();
  };

  return (
    <DocumentsTable
      documents={documentsWithStatus}
      isLoading={isLoading}
      error={error}
      onRetry={refetch}
      onCategoryUpdate={handleCategoryUpdate}
      compact={compact}
      highlightId={highlightId}
    />
  );
};

export default Documents;
